/**
 * The operator console.
 *
 * **The nav is named after what the operator is trying to DO.** The previous build had one item
 * per API family — Overview, Agents, Usage, Activity, Roles, Spend, Settings — which is a
 * faithful map of the backend and a list of things nobody arrives wanting. People arrive
 * wanting one of a few things, so there are a few.
 *
 * And it has real URLs. The previous build tracked the view in `useState`, which means no deep
 * link to an agent, no back button, and no way to send somebody the approval you want them to
 * look at.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { BASE_OF, PRODUCT, Router, TOOLS, identifyTenant, match, resetAnalytics, usePageViews, useRoute, useTheme } from "@authoxi/ui-shared";
import { Api } from "./api";
import { currentSession, signOut, type SessionInfo } from "./session";
import { KeyGate } from "./views/KeyGate";
import { Today } from "./views/Today";
import { Approvals } from "./views/Approvals";
import { AgentDetail, Agents } from "./views/Agents";
import { Users } from "./views/Users";
import { Spend } from "./views/Spend";
import { Evidence } from "./views/Evidence";
import { Settings } from "./views/Settings";
import { Setup } from "./views/Setup";

// NO STORAGE KEYS. There were two — `authoxi.sk` and `authoxi.actor` — and the first held a
// long-lived `sk_live_` where any script on this origin could read it. The session now lives in
// an HttpOnly cookie the server sets and this bundle cannot see; see ./session.ts.

export const NAV = [
  ["/", "Today", "today"],
  ["/approvals", "Approvals", "approvals"],
  ["/agents", "Agents", "agents"],
  ["/users", "Users", "users"],
  ["/spend", "Spend", "spend"],
  ["/evidence", "Evidence", "evidence"],
  ["/settings", "Settings", "settings"],
  ["/setup", "Set up", "setup"],
] as const;

const ICONS: Record<string, string> = {
  today: "M4 5.5h16v14H4z M4 9.5h16M9 3v4M15 3v4",
  approvals: "M5 12.5 10 17l9-10",
  agents: "M12 5a3.2 3.2 0 1 0 0 6.4A3.2 3.2 0 0 0 12 5z M5 20a7 7 0 0 1 14 0",
  // Two figures, not one — the agents icon is a single silhouette, and these two
  // screens sit next to each other in the nav.
  users: "M9 4.5a3 3 0 1 0 0 6 3 3 0 0 0 0-6z M2.5 19.5a6.5 6.5 0 0 1 13 0 M16 4.9a3 3 0 0 1 0 5.9 M17.5 13.6a6.5 6.5 0 0 1 4 5.9",
  spend: "M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17z M12 7v10M9.5 9.5h5M9.5 14.5h5",
  evidence: "M6 3h8l4 4v14H6z M14 3v4h4M9 13l2 2 4-4",
  settings: "M4 6h16M4 12h16M4 18h16",
  setup: "M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z M12 2v3M12 19v3M2 12h3M19 12h3",
};

export default function App() {
  // `undefined` means "we have not asked yet", which is a third state and needs to be one: with
  // the key in localStorage the answer was synchronous, and now it is a round trip. Collapsing
  // it into `null` would flash the sign-in screen at somebody who is already signed in, on
  // every single load.
  const [session, setSession] = useState<SessionInfo | null | undefined>(undefined);

  useEffect(() => {
    let live = true;
    currentSession()
      .then((s) => live && setSession(s))
      .catch(() => live && setSession(null));
    return () => {
      live = false;
    };
  }, []);

  if (session === undefined) {
    // Deliberately not a spinner. This resolves in one same-origin round trip, and a spinner
    // that appears for 40ms on every load reads as slowness rather than as work.
    return <div className="gate" aria-busy="true" />;
  }

  if (!session) {
    return <KeyGate onSignedIn={setSession} />;
  }

  return (
    <Router base={BASE_OF.console}>
      <Shell
        actor={session.actor ?? ""}
        onSignOut={async () => {
          // Server first. Clearing local state while the session still resolves would leave a
          // credential that works for anyone who captured the cookie.
          await signOut();
          // Then forget them here. Without this the next operator to use this browser inherits
          // the last one's analytics identity, and every funnel silently merges two people.
          resetAnalytics();
          setSession(null);
        }}
      />
    </Router>
  );
}

function Shell({
  actor,
  onSignOut,
}: {
  actor: string;
  onSignOut: () => void | Promise<void>;
}) {
  const { path } = useRoute();
  const [, toggleTheme] = useTheme();
  // NOT cookieless here, unlike the public pages. This surface is behind a tenant key, the
  // operator has an account, and "which screens does an operator actually use" is a question
  // about a returning person — which memory-only persistence cannot answer.
  usePageViews(path, { cookieless: false });
  // No arguments, and that is the point: there is no credential in this bundle to pass. The
  // actor was a browser-set header, which meant a browser could claim to be anyone; the server
  // recorded it at sign-in and stamps it on every proxied call now.
  const api = useMemo(() => new Api(), []);

  const [tenant, setTenant] = useState<{ name: string; tenant_id: string; status: string } | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [fatal, setFatal] = useState<string | null>(null);

  const refreshChrome = useCallback(async () => {
    try {
      const [t, p] = await Promise.all([api.tenant(), api.pending()]);
      setTenant(t);
      // The TENANT, never the operator. A tenant id is the account we already bill and support;
      // an individual's name or email is a person, and putting one in an analytics vendor is a
      // different promise from the one /privacy makes.
      identifyTenant(t.tenant_id, { tenant_status: t.status });
      setPendingCount(p.pending.filter((x) => x.status === "pending").length);
      setFatal(null);
    } catch (e) {
      setFatal(e instanceof Error ? e.message : String(e));
    }
  }, [api]);

  useEffect(() => {
    void refreshChrome();
  }, [refreshChrome]);

  if (fatal && !tenant) {
    return (
      <div className="gate">
        <div className="gate-card">
          <h1>Can't reach the API</h1>
          <div className="err" data-testid="fatal-error">
            {fatal}
          </div>
          <p>
            If the key is wrong, sign out and paste a valid one. If the stack is down, bring it up
            with <span className="mono">make up</span>.
          </p>
          <button className="btn" onClick={onSignOut} data-testid="signout">
            Use a different key
          </button>
        </div>
      </div>
    );
  }

  const view =
    match(path, [
      ["/", () => <Today api={api} onResolved={refreshChrome} />],
      ["/approvals", () => <Approvals api={api} onResolved={refreshChrome} />],
      ["/agents", () => <Agents api={api} />],
      ["/agents/:id", (p) => <AgentDetail api={api} agentId={p.id} />],
      ["/users", () => <Users api={api} />],
      ["/spend", () => <Spend api={api} />],
      ["/evidence", () => <Evidence api={api} />],
      ["/settings", () => <Settings api={api} />],
      ["/setup", () => <Setup api={api} />],
    ]) ?? <NotFound />;

  return (
    <div className="app">
      <aside className="side">
        {/* The way out. An operator deep in the console had no link back to anything — the
            wordmark was a `<b>`, so the one element every visitor tries first did nothing. */}
        <div className="brand">
          <a href={PRODUCT}>
            <b>authoxi</b>
          </a>
          <a className="brand-tools" href={TOOLS}>
            free tools
          </a>
        </div>

        <div className="tenant" data-testid="tenant-card">
          <span className="av">{(tenant?.name ?? "?")[0]?.toUpperCase()}</span>
          <span>
            <span className="nm" data-testid="tenant-name">
              {tenant?.name ?? "…"}
            </span>
            <br />
            <span className="en">{tenant?.tenant_id ?? ""}</span>
          </span>
        </div>

        <nav className="nav">
          {NAV.map(([href, label, icon]) => (
            <a
              key={href}
              href={`/console${href === "/" ? "/" : href}`}
              className={path === href || (href !== "/" && path.startsWith(href)) ? "on" : ""}
              data-testid={`nav-${icon}`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d={ICONS[icon]} />
              </svg>
              <span>{label}</span>
              {icon === "approvals" && pendingCount > 0 && (
                <span className="nav-count" data-testid="pending-badge">
                  {pendingCount}
                </span>
              )}
            </a>
          ))}
        </nav>

        <div className="side-foot">
          <button className="theme-btn" onClick={toggleTheme} aria-label="Switch theme">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 13.2A8 8 0 0 1 10.8 4a8 8 0 1 0 9.2 9.2Z" />
            </svg>
          </button>
          {/* Says who, and says where the key is. Both are answers an operator would otherwise
              have to guess at — and the second one is the promise this console now keeps. */}
          {actor ? <>Signed in as {actor}. </> : null}
          Key held by the server, not this browser.{" "}
          <button className="link" onClick={onSignOut} data-testid="signout">
            Sign out
          </button>
        </div>
      </aside>

      <main className="main">{view}</main>
    </div>
  );
}

function NotFound() {
  return (
    <div className="view-head">
      <h1>No such screen.</h1>
      <p>
        <a href="/console/">Back to Today</a>.
      </p>
    </div>
  );
}
